import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArchiveIcon from '@mui/icons-material/Archive';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import apiService from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
}));

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (!trimmed) return '';
  return trimmed.startsWith('http')
    ? trimmed
    : `${apiService.baseURL || ''}${trimmed}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateOnly = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const addMonths = (date, months) => {
  const next = new Date(date.getTime());
  const originalDay = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() !== originalDay) {
    next.setDate(0);
  }
  return next;
};

const getArchiveRemovalStatus = (createdAt) => {
  const createdDate = new Date(createdAt || '');
  if (Number.isNaN(createdDate.getTime())) {
    return {
      label: 'Removal date unknown',
      color: 'default',
      helper: 'Missing archive date',
    };
  }

  const removalDate = addMonths(createdDate, 6);
  const now = new Date();
  const daysRemaining = Math.ceil((removalDate.getTime() - now.getTime()) / 86400000);
  const removalLabel = formatDateOnly(removalDate);

  if (daysRemaining <= 0) {
    return {
      label: 'Removal due',
      color: 'error',
      helper: `Scheduled after ${removalLabel}`,
    };
  }

  if (daysRemaining <= 30) {
    return {
      label: `Removes in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`,
      color: 'warning',
      helper: `Removal date ${removalLabel}`,
    };
  }

  return {
    label: `Removes ${removalLabel}`,
    color: 'success',
    helper: '6-month archive status',
  };
};

const formatBytes = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let nextSize = size;
  let unitIndex = 0;
  while (nextSize >= 1024 && unitIndex < units.length - 1) {
    nextSize /= 1024;
    unitIndex += 1;
  }
  return `${nextSize.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const Archive = ({ onNavigate }) => {
  const [projects, setProjects] = useState([]);
  const [buildingArchives, setBuildingArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoringTargetKey, setRestoringTargetKey] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const loadArchives = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getProjectsWithStats();
      const projectList = Array.isArray(data) ? data : [];
      setProjects(projectList);
      const buildingArchiveGroups = await Promise.all(
        projectList.map(async (project) => {
          try {
            const buildings = await apiService.getProjectBuildings(project.id);
            return (Array.isArray(buildings) ? buildings : [])
              .filter((building) => Boolean(building.archive_exists && building.latest_archive_url))
              .map((building) => ({
                ...building,
                project_name: project.name,
                project_address: project.address_detail,
              }));
          } catch (buildingErr) {
            console.warn('Error loading archived buildings for project:', project.id, buildingErr);
            return [];
          }
        })
      );
      setBuildingArchives(buildingArchiveGroups.flat());
    } catch (err) {
      console.error('Error loading archives:', err);
      setError('Failed to load archives. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchives();
  }, []);

  const archivedProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return projects
      .filter((project) => Boolean(project.archive_exists && project.latest_archive_url))
      .filter((project) => {
        if (!query) return true;
        return [
          project.name,
          project.address_detail,
          project.facade_inspector,
          project.latest_archive_filename,
          project.project_type,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      })
      .sort((a, b) => {
        const dateA = new Date(a.latest_archive_created_at || 0).getTime();
        const dateB = new Date(b.latest_archive_created_at || 0).getTime();
        const normalizedA = Number.isNaN(dateA) ? 0 : dateA;
        const normalizedB = Number.isNaN(dateB) ? 0 : dateB;
        return normalizedB - normalizedA;
      });
  }, [projects, searchQuery]);

  const archivedBuildings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return buildingArchives
      .filter((building) => {
        if (!query) return true;
        return [
          building.name,
          building.address,
          building.project_name,
          building.project_address,
          building.latest_archive_filename,
        ].some((value) => String(value || '').toLowerCase().includes(query));
      })
      .sort((a, b) => {
        const dateA = new Date(a.latest_archive_created_at || 0).getTime();
        const dateB = new Date(b.latest_archive_created_at || 0).getTime();
        const normalizedA = Number.isNaN(dateA) ? 0 : dateA;
        const normalizedB = Number.isNaN(dateB) ? 0 : dateB;
        return normalizedB - normalizedA;
      });
  }, [buildingArchives, searchQuery]);

  const handleOpenArchive = (project) => {
    const targetUrl = toAbsoluteUrl(project.latest_archive_url);
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenBuildingArchive = (building) => {
    const targetUrl = toAbsoluteUrl(building.latest_archive_url);
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenProject = (project) => {
    if (!onNavigate) return;
    onNavigate('buildings', {
      projectId: project.id,
      projectName: project.name,
    });
  };

  const handleOpenBuilding = (building) => {
    if (!onNavigate) return;
    onNavigate('elevations', {
      projectId: building.project_id,
      projectName: building.project_name,
      buildingId: building.id,
      buildingName: building.name,
    });
  };

  const handleRestoreArchive = async () => {
    const target = restoreTarget;
    if (!target?.item) return;
    const isBuilding = target.type === 'building';
    const item = target.item;
    const targetKey = `${target.type}-${item.id}`;

    try {
      setRestoringTargetKey(targetKey);
      setError('');
      const result = isBuilding
        ? await apiService.restoreBuildingArchive(item.id)
        : await apiService.restoreProjectArchive(item.id);
      const restoredCount = result?.deleted_archive_count || 0;
      setSuccessMessage(`"${item.name}" restored${restoredCount ? ` (${restoredCount} archive file${restoredCount === 1 ? '' : 's'} removed)` : ''}.`);
      setRestoreTarget(null);
      await loadArchives();
    } catch (err) {
      console.error('Error restoring archive:', err);
      setError(`Failed to restore "${item.name}". Please try again.`);
    } finally {
      setRestoringTargetKey(null);
    }
  };

  const totalArchiveSize = archivedProjects.reduce((sum, project) => sum + (project.latest_archive_size || 0), 0)
    + archivedBuildings.reduce((sum, building) => sum + (building.latest_archive_size || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="#333">
            Archive
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {archivedProjects.length} archived project{archivedProjects.length === 1 ? '' : 's'} · {archivedBuildings.length} archived building{archivedBuildings.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadArchives}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <StyledCard>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2}>
            <TextField
              size="small"
              placeholder="Search archives"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Chip
              icon={<ArchiveIcon />}
              label={`Total archive size: ${totalArchiveSize ? formatBytes(totalArchiveSize) : '-'}`}
              color="primary"
              variant="outlined"
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>PROJECT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ARCHIVE FILE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ARCHIVED AT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>SIZE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>VERSIONS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
                        <CircularProgress size={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && archivedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box textAlign="center" py={5}>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery.trim() ? 'No archived projects match your search.' : 'No archived projects found yet.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && archivedProjects.map((project) => {
                  const status = getArchiveRemovalStatus(project.latest_archive_created_at);
                  return (
                    <TableRow key={project.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{project.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {project.project_type || 'Project'}{project.address_detail ? ` - ${project.address_detail}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {project.latest_archive_filename || '-'}
                      </TableCell>
                      <TableCell>{formatDate(project.latest_archive_created_at)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Chip size="small" color={status.color} label={status.label} variant="outlined" />
                          <Typography variant="caption" color="text.secondary">{status.helper}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatBytes(project.latest_archive_size)}</TableCell>
                      <TableCell>{project.latest_archive_file_count || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleOpenArchive(project)}
                          >
                            Open
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={restoringTargetKey === `project-${project.id}` ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
                            onClick={() => setRestoreTarget({ type: 'project', item: project })}
                            disabled={restoringTargetKey === `project-${project.id}`}
                          >
                            Restore
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>

      <StyledCard sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Building Archives</Typography>
              <Typography variant="body2" color="text.secondary">
                {archivedBuildings.length} archived building{archivedBuildings.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Chip
              icon={<ArchiveIcon />}
              label={`Building archive size: ${archivedBuildings.reduce((sum, building) => sum + (building.latest_archive_size || 0), 0) ? formatBytes(archivedBuildings.reduce((sum, building) => sum + (building.latest_archive_size || 0), 0)) : '-'}`}
              color="primary"
              variant="outlined"
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>BUILDING</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>PROJECT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ARCHIVE FILE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>ARCHIVED AT</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>SIZE</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>VERSIONS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box display="flex" justifyContent="center" alignItems="center" py={5}>
                        <CircularProgress size={28} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && archivedBuildings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box textAlign="center" py={5}>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery.trim() ? 'No archived buildings match your search.' : 'No archived buildings found yet.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && archivedBuildings.map((building) => {
                  const status = getArchiveRemovalStatus(building.latest_archive_created_at);
                  return (
                    <TableRow key={building.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{building.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {building.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{building.project_name || `Project #${building.project_id}`}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {building.latest_archive_filename || '-'}
                      </TableCell>
                      <TableCell>{formatDate(building.latest_archive_created_at)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Chip size="small" color={status.color} label={status.label} variant="outlined" />
                          <Typography variant="caption" color="text.secondary">{status.helper}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatBytes(building.latest_archive_size)}</TableCell>
                      <TableCell>{building.latest_archive_file_count || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleOpenBuildingArchive(building)}
                          >
                            Open
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={restoringTargetKey === `building-${building.id}` ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
                            onClick={() => setRestoreTarget({ type: 'building', item: building })}
                            disabled={restoringTargetKey === `building-${building.id}`}
                          >
                            Restore
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </StyledCard>

      <Dialog open={Boolean(restoreTarget)} onClose={() => setRestoreTarget(null)}>
        <DialogTitle>Restore {restoreTarget?.type === 'building' ? 'Building' : 'Project'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Restore "{restoreTarget?.item?.name}". This removes the stored archive ZIP file(s), but keeps the underlying project, building, images, and reports.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleRestoreArchive}
            disabled={Boolean(restoringTargetKey)}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Archive;
